import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../config";

const ProductRow = ({ index, product, handleProductChange, removeRow, products, handleProductSelect, errors }) => (
  <div key={index} className="flex gap-4 items-center mb-3">
    <div className="relative w-full sm:w-1/3">
      <select
        value={product.product}
        onChange={(e) => handleProductSelect(index, e.target.value)}
        className="border rounded px-2 py-1 w-full"
      >
        <option value="">Select Product</option>
        {products.map((prod, i) => (
          <option key={i} value={prod.name}>
            {prod.name}
          </option>
        ))}
      </select>
      {errors[`product-${index}`] && <p className="text-red-500 text-xs">{errors[`product-${index}`]}</p>}
    </div>

    <input
      type="number"
      placeholder="Quantity"
      value={product.quantity}
      onChange={(e) => handleProductChange(index, "quantity", e.target.value)}
      className="border rounded px-2 py-1 w-full sm:w-1/3"
    />
    {errors[`quantity-${index}`] && <p className="text-red-500 text-xs">{errors[`quantity-${index}`]}</p>}

    <input
      type="number"
      placeholder="Price"
      value={product.price}
      onChange={(e) => handleProductChange(index, "price", e.target.value)}
      className="border rounded px-2 py-1 w-full sm:w-1/3"
    />
    {errors[`price-${index}`] && <p className="text-red-500 text-xs">{errors[`price-${index}`]}</p>}

    <button onClick={() => removeRow(index)} className="text-red-500 px-2 py-1 border rounded">
      Remove
    </button>
  </div>
);

export default function AddOrder() {
  const [orderProducts, setOrderProducts] = useState([{ productId: "", product: "", quantity: "", price: "" }]);
  const [formData, setFormData] = useState({ deliveryDate: "", customerName: "", customerAddress: "", customerId: "", });
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    axios.get(`${config.baseUrl}${config.apiEndpoints.products}`).then((response) => setProducts(response.data));
    axios.get(`${config.baseUrl}${config.apiEndpoints.customers}`).then((response) => setCustomers(response.data));
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...orderProducts];
    updatedProducts[index][field] = value;
    setOrderProducts(updatedProducts);
  };

  const handleProductSelect = (index, productName) => {
    const selectedProduct = products.find((product) => product.name === productName);
    if (selectedProduct) {
      const updatedProducts = [...orderProducts];
      updatedProducts[index] = {
        ...updatedProducts[index],
        product: selectedProduct.name,
        productId: selectedProduct.id, // Store productId
        price: selectedProduct.price, // Automatically set price
      };
      setOrderProducts(updatedProducts);
    }
  };


  const handleSelectCustomer = (customerName) => {
    const selectedCustomer = customers.find((customer) => customer.name === customerName);
    if (selectedCustomer) {
      setFormData({ ...formData, customerName: selectedCustomer.name, customerAddress: selectedCustomer.address, customerId: selectedCustomer.id, });
    }
  };

  const addRow = () => setOrderProducts([...orderProducts, { product: "", quantity: "", price: "" }]);

  const removeRow = (index) => setOrderProducts(orderProducts.filter((_, i) => i !== index));

  const validateInputs = () => {
    const errors = {};
    // if (!formData.deliveryDate) errors.deliveryDate = "Delivery date is required";
    if (!formData.customerName) errors.customerName = "Customer name is required";
    if (!formData.customerAddress) errors.customerAddress = "Customer address is required";

    orderProducts.forEach((product, index) => {
      if (!product.product) errors[`product-${index}`] = "Product is required";
      if (!product.quantity || isNaN(product.quantity) || product.quantity <= 0)
        errors[`quantity-${index}`] = "Valid quantity is required";
      if (!product.price || isNaN(product.price) || product.price <= 0)
        errors[`price-${index}`] = "Valid price is required";
    });

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateInputs()) {
      axios
        .post(`${config.baseUrl}${config.apiEndpoints.createOrder}`, { ...formData, orderItems: orderProducts })
        .then(() => {
          alert("Order added successfully");
          setFormData({ deliveryDate: "", customerName: "", customerAddress: "", customerId: "", });
          setOrderProducts([{ product: "", quantity: "", price: "" }]);
        })
        .catch((error) => alert(`An error occurred: ${error.message}`));
    }
  };

  // Grand total calculation
  const grandTotal = orderProducts.reduce(
    (total, product) =>
      total + (parseFloat(product.quantity) || 0) * (parseFloat(product.price) || 0),
    0
  ).toFixed(2);
  

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 max-w-4xl mx-auto p-2">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">New Order</h3>
        <button
          type="submit"
          onClick={handleSubmit}
          className="bg-blue-500 text-white px-4 py-2 rounded"
        >
          Save Order
        </button>
      </div>

      <form className="space-y-6">
        <div>
          <div>
            <label className="block mb-2">Customer Name</label>
            <select
              name="customerName"
              value={formData.customerName}
              onChange={(e) => handleSelectCustomer(e.target.value)}
              className="border rounded px-2 py-1 w-full"
            >
              <option value="">Select Customer</option>
              {customers.map((customer, i) => (
                <option key={i} value={customer.name}>
                  {customer.name}
                </option>
              ))}
            </select>

            {errors.customerName && <p className="text-red-500 text-xs">{errors.customerName}</p>}
          </div>

          <div>
            <label className="block mb-2">Customer Address</label>
            <textarea
              name="customerAddress"
              value={formData.customerAddress}
              onChange={handleInputChange}
              className="border rounded px-2 py-1 w-full"
            ></textarea>
            {errors.customerAddress && <p className="text-red-500 text-xs">{errors.customerAddress}</p>}
          </div>

          <h4 className="font-semibold mb-2">Order Products</h4>
          {orderProducts.map((product, index) => (
            <ProductRow
              key={index}
              index={index}
              product={product}
              handleProductChange={handleProductChange}
              removeRow={removeRow}
              products={products}
              handleProductSelect={handleProductSelect}
              errors={errors}
            />
          ))}
          <button type="button" onClick={addRow} className="bg-blue-500 text-white p-1 rounded">
            Add Row
          </button>
        </div>

        <div>
          <label className="block mb-2">Delivery Date</label>
          <input
            type="date"
            name="deliveryDate"
            value={formData.deliveryDate}
            onChange={handleInputChange}
            className="border rounded px-2 py-1 w-full"
          />
          {errors.deliveryDate && <p className="text-red-500 text-xs">{errors.deliveryDate}</p>}
        </div>

        <div className="flex justify-between items-center mt-4">
          <span className="font-semibold">Grand Total: KES {grandTotal}</span>
        </div>
      </form>
    </div>
  );
}
